import React from "react";
import { Alert, AlertColor, Box, Stack } from "@mui/material";
import CclDrawer from "../../../../../../../components/common/cclDrawer/cclDrawer";
import CclInfoTable, {
    CclInfoTableFormValue,
    CclInfoTableRowTemplate,
} from "../../../../../../../components/common/cclInfoTable/cclInfoTable";
import CclButton from "../../../../../../../components/common/cclButtons/cclButton";
import CclDrawerActionBar from "../../../../../../../components/common/cclDrawer/cclDrawerActionBar";
import { CclInfoTblParticipantKeyDatesInfoTemplate } from "./cclInfoTblParticipantKeyDatesInfoTemplate";
import {
    useGetParticipantKeyDatesQuery,
    useSetParticipantAccessDateMutation,
    useSetParticipantDeadlineDateMutation,
    useSetParticipantInvitationDateMutation,
} from "../../../../../../../services/cclTokenedEnterpriseParticipantApi";
import { eInfoTableValueFieldEditType } from "../../../../../../../components/common/cclInfoTable/cclInfoTableValueField";
import ComponentLoader from "../../../../../../../components/common/componentLoader";
import { AccessEventRegistrationDetails } from "../../../../../../../services/types/accessEventTypes";
import { Participant } from "../../../../../../../services/types/enterpriseParticipantApiTypes";
import useLogAccessEvent from "../../../../../../../hooks/useLogAccessEvent";
import {
    getLocalIsoDateWithNoTime,
    parseDateAsLocal,
} from "../../../../../../../utilities/dateHelpers";

export type ParticipantKeyDatesDrawerProps = {
    open: boolean;
    onClose: () => void;
    participant: Participant;
    showSnackbar: (message: string, severity: AlertColor) => void;
    readOnly?: boolean;
    onSaved?: () => void;
};

const ParticipantKeyDatesDrawer: React.FC<ParticipantKeyDatesDrawerProps> = (props) => {
    type PaxKeyDatesType = {
        registration: Date | undefined | null | string;
        accessExpiration: Date | undefined | null | string;
        actualPreworkInvitation: Date | undefined | null | string;
        deadline: Date | undefined | null | string;
        scheduledPreworkInvitation: Date | undefined | null | string;
    };
    const { logEvent } = useLogAccessEvent();

    // local state
    const [dates, setDates] = React.useState<PaxKeyDatesType | undefined>(undefined);
    const [changes, setChanges] = React.useState<CclInfoTableFormValue[]>([]);
    const [template, setTemplate] = React.useState<CclInfoTableRowTemplate[]>(
        CclInfoTblParticipantKeyDatesInfoTemplate,
    );

    // api calls
    const {
        data: participantDates,
        isSuccess,
        isLoading,
        isError,
    } = useGetParticipantKeyDatesQuery(props.participant.esiKey, {
        skip: props.participant.esiKey == null,
    });
    const [updateAccessExpirationDate] = useSetParticipantAccessDateMutation();
    const [updateInvitationDate] = useSetParticipantInvitationDateMutation();
    const [updateDeadlineDate] = useSetParticipantDeadlineDateMutation();

    React.useEffect(() => {
        if (isSuccess && participantDates !== undefined) {
            var newDates: PaxKeyDatesType = {
                registration: undefined,
                accessExpiration: undefined,
                actualPreworkInvitation: "",
                deadline: "",
                scheduledPreworkInvitation: undefined,
            };

            var newTemplate = CclInfoTblParticipantKeyDatesInfoTemplate.map((item) => {
                var val: string | Date | undefined | null = undefined;
                var canEdit = false;
                if (item.id === "registration") {
                    newDates.registration =
                        participantDates.registration.date == null
                            ? ""
                            : parseDateAsLocal(
                                  new Date(participantDates.registration.date),
                              ).toLocaleDateString();
                } else if (item.id === "accessExpiration") {
                    canEdit = !props.readOnly && participantDates.accessExpiration.editable;
                    if (canEdit) {
                        val =
                            participantDates.accessExpiration.date == null
                                ? null
                                : parseDateAsLocal(
                                      new Date(participantDates.accessExpiration.date),
                                  );

                        item.valueField.minDate = participantDates.accessExpiration.minDate
                            ? parseDateAsLocal(new Date(participantDates.accessExpiration.minDate))
                            : undefined;

                        item.valueField.maxDate = participantDates.accessExpiration.maxDate
                            ? parseDateAsLocal(new Date(participantDates.accessExpiration.maxDate))
                            : undefined;
                    } else {
                        val =
                            participantDates.accessExpiration.date == null
                                ? ""
                                : parseDateAsLocal(
                                      new Date(participantDates.accessExpiration.date),
                                  ).toLocaleDateString();
                    }
                    newDates.accessExpiration = val;
                } else if (item.id === "actualPreworkInvitation") {
                    canEdit = !props.readOnly && participantDates.actualPreworkInvitation.editable;
                    if (canEdit) {
                        val =
                            participantDates.actualPreworkInvitation.date == null
                                ? null
                                : parseDateAsLocal(
                                      new Date(participantDates.actualPreworkInvitation.date),
                                  );
                        item.valueField.minDate = participantDates.actualPreworkInvitation.minDate
                            ? parseDateAsLocal(
                                  new Date(participantDates.actualPreworkInvitation.minDate),
                              )
                            : undefined;

                        item.valueField.maxDate = participantDates.actualPreworkInvitation.maxDate
                            ? parseDateAsLocal(
                                  new Date(participantDates.actualPreworkInvitation.maxDate),
                              )
                            : undefined;
                    } else {
                        val =
                            participantDates.actualPreworkInvitation.date == null
                                ? ""
                                : parseDateAsLocal(
                                      new Date(participantDates.actualPreworkInvitation.date),
                                  ).toLocaleDateString();
                    }
                    newDates.actualPreworkInvitation = val;
                } else if (item.id === "deadline") {
                    canEdit = !props.readOnly && participantDates.deadline.editable;
                    if (canEdit) {
                        val =
                            participantDates.deadline.date == null
                                ? null
                                : parseDateAsLocal(new Date(participantDates.deadline.date));

                        item.valueField.minDate = participantDates.deadline.minDate
                            ? parseDateAsLocal(new Date(participantDates.deadline.minDate))
                            : undefined;

                        item.valueField.maxDate = participantDates.deadline.maxDate
                            ? parseDateAsLocal(new Date(participantDates.deadline.maxDate))
                            : undefined;
                        item.required = true;
                    } else {
                        val =
                            participantDates.deadline.date == null
                                ? ""
                                : parseDateAsLocal(
                                      new Date(participantDates.deadline.date),
                                  ).toLocaleDateString();
                    }
                    newDates.deadline = val;
                } else if (item.id === "scheduledPreworkInvitation") {
                    canEdit = !props.readOnly && participantDates.scheduledPreworkInvitation.editable;
                    if (canEdit) {
                        val =
                            participantDates.scheduledPreworkInvitation.date == null
                                ? null
                                : parseDateAsLocal(
                                      new Date(participantDates.scheduledPreworkInvitation.date),
                                  );

                        item.valueField.minDate = participantDates.scheduledPreworkInvitation
                            .minDate
                            ? parseDateAsLocal(
                                  new Date(participantDates.scheduledPreworkInvitation.minDate),
                              )
                            : undefined;

                        item.valueField.maxDate = participantDates.scheduledPreworkInvitation
                            .maxDate
                            ? parseDateAsLocal(
                                  new Date(participantDates.scheduledPreworkInvitation.maxDate),
                              )
                            : undefined;
                    } else {
                        val =
                            participantDates.scheduledPreworkInvitation.date == null
                                ? ""
                                : parseDateAsLocal(
                                      new Date(participantDates.scheduledPreworkInvitation.date),
                                  ).toLocaleDateString();
                    }
                    newDates.scheduledPreworkInvitation = val;
                }

                item.editable = canEdit;
                item.fieldType = canEdit ? "Date" : "string";
                item.valueField.editFieldType = canEdit
                    ? eInfoTableValueFieldEditType.DatePicker
                    : eInfoTableValueFieldEditType.Text;
                return item;
            });
            setTemplate(newTemplate);
            setDates(newDates);
            setChanges([]);
        }
    }, [isSuccess, participantDates]);

    const changed = (changes: CclInfoTableFormValue[]) => {
        setChanges(changes);
    };

    const logDatesEditedEvent = () => {
        let eventData: AccessEventRegistrationDetails = {
            esiKey: props.participant.esiKey.toString(),
            imKey: props.participant.imKey.toString(),
            email: props.participant.emailAddress,
        };
        logEvent("ParticipantDatesEdited", eventData);
    };

    const saveInvitationDate = (date: Date | null) => {
        return updateInvitationDate({
            esiKey: props.participant.esiKey,
            esKey: props.participant.esKey,
            payload: { InvitationDate: date != null ? getLocalIsoDateWithNoTime(date) : null },
        })
            .unwrap()
            .then(() => {
                logDatesEditedEvent();
                return true;
            })
            .catch(() => false);
    };

    const saveAccessExpirationDate = (date: Date) => {
        return updateAccessExpirationDate({
            esiKey: props.participant.esiKey,
            esKey: props.participant.esKey,
            payload: { AccessDate: getLocalIsoDateWithNoTime(date) },
        })
            .unwrap()
            .then(() => {
                logDatesEditedEvent();
                return true;
            })
            .catch(() => false);
    };

    const saveDeadlineDate = (date: Date) => {
        return updateDeadlineDate({
            esiKey: props.participant.esiKey,
            esKey: props.participant.esKey,
            payload: { DeadlineDate: getLocalIsoDateWithNoTime(date) },
        })
            .unwrap()
            .then(() => {
                logDatesEditedEvent();
                return true;
            })
            .catch(() => false);
    };

    const onSave = () => {
        if (changes.length === 0) return;

        const pendingSaves: Promise<boolean>[] = [];

        for (var i = 0; i < changes.length; i++) {
            var change = changes[i];

            if (change.name === "accessExpiration") {
                pendingSaves.push(saveAccessExpirationDate(new Date(change.value)));
            } else if (change.name === "scheduledPreworkInvitation") {
                pendingSaves.push(
                    saveInvitationDate(change.value != null ? new Date(change.value) : null),
                );
            } else if (change.name === "deadline") {
                pendingSaves.push(saveDeadlineDate(new Date(change.value)));
            }
        }

        if (pendingSaves.length === 0) {
            props.showSnackbar("Selected Field(s) Cannot Be Saved", "error");
            return;
        }

        Promise.all(pendingSaves).then((results) => {
            const succeededCount = results.filter((succeeded) => succeeded).length;

            if (succeededCount === results.length) {
                props.showSnackbar("Key Dates Updated", "success");
            } else if (succeededCount === 0) {
                props.showSnackbar("Key Dates Update Failed", "error");
            } else {
                props.showSnackbar(
                    `${succeededCount} of ${results.length} Key Dates Updated`,
                    "warning",
                );
            }

            if (succeededCount > 0) {
                props.onSaved?.();
            }
            props.onClose();
        });

        setChanges([]);
    };

    return (
        <CclDrawer open={props.open} onClose={props.onClose} title="Participant Dates">
            <Box>
                <Stack spacing={2}>
                    {isError === true ? (
                        <Alert severity="error">Could not retrieve participant dates</Alert>
                    ) : null}
                    {isLoading === true ? <ComponentLoader msg={""} /> : null}
                    {isSuccess && participantDates !== undefined && dates !== undefined ? (
                        <CclInfoTable
                            template={template}
                            data={dates}
                            hideSave={true}
                            onChange={changed}
                        />
                    ) : null}

                    <CclDrawerActionBar>
                        {(isSuccess || isLoading) && !props.readOnly ? (
                            <CclButton
                                onClick={onSave}
                                mode={"primary"}
                                disabled={changes.length === 0}
                            >
                                Save
                            </CclButton>
                        ) : (
                            <></>
                        )}
                        <CclButton onClick={props.onClose} mode={"secondary"}>
                            Close
                        </CclButton>
                    </CclDrawerActionBar>
                </Stack>
            </Box>
        </CclDrawer>
    );
};

export default ParticipantKeyDatesDrawer;
